import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/content";

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.1 } },
};

const card = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

const CoreServicesSection = () => {
    return (
        <section id="services" data-testid="services-section" className="relative py-24 sm:py-28">
            <div className="pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden="true"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(217,164,65,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(217,164,65,0.05) 1px, transparent 1px)",
                    backgroundSize: "72px 72px",
                }}
            />
            <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mx-auto max-w-3xl text-center"
                >
                    <div className="flex items-center justify-center gap-4">
                        <span className="gold-hairline h-px w-14" />
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D9A441]">Multiple Pathways. One Mission. Your Empowerment.</p>
                        <span className="gold-hairline h-px w-14" />
                    </div>
                    <h2 data-testid="services-heading" className="mt-6 font-display text-3xl font-bold tracking-tight text-[#F8F5EE] sm:text-4xl lg:text-5xl">
                        Core <span className="text-gold-gradient">Services</span>
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-[#F8F5EE]/60 sm:text-lg">
                        Explore our services designed to educate, assist, and equip you with the knowledge, tools, and support you need.
                    </p>
                </motion.div>

                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-60px" }}
                    className="mt-16 grid gap-6 sm:grid-cols-2 xl:grid-cols-3"
                >
                    {SERVICES.map((service) => (
                        <motion.article
                            key={service.key}
                            variants={card}
                            data-testid={`service-card-${service.key}`}
                            className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:bg-white/[0.05]"
                            style={{ "--accent": service.accent }}
                            whileHover={{ boxShadow: `0 24px 60px -18px ${service.accent}66` }}
                        >
                            <div className="relative h-48 overflow-hidden">
                                <img
                                    src={service.image}
                                    alt={service.alt}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#080B12] via-[#080B12]/25 to-transparent" />
                                <span
                                    data-testid={`service-card-badge-${service.key}`}
                                    className="absolute left-4 top-4 rounded-full px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur-md"
                                    style={{ backgroundColor: `${service.accentDeep}D9`, border: `1px solid ${service.accent}` }}
                                >
                                    {service.badge}
                                </span>
                            </div>

                            <div className="flex flex-1 flex-col p-6">
                                <h3 className="font-display text-xl font-bold tracking-wide text-[#F8F5EE]">{service.title}</h3>
                                <p className="mt-2 text-[11px] font-semibold uppercase leading-relaxed tracking-[0.16em]" style={{ color: service.accent }}>
                                    {service.headline}
                                </p>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#F8F5EE]/60">{service.description}</p>
                                <a
                                    data-testid={`service-card-cta-${service.key}`}
                                    href={service.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-shine mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-transform duration-300 hover:scale-[1.03]"
                                    style={{
                                        backgroundColor: service.accentDeep,
                                        color: service.ctaTextDark ? "#080B12" : "#FFFFFF",
                                        boxShadow: `0 8px 26px -10px ${service.accent}99`,
                                    }}
                                >
                                    {service.cta}
                                    <ArrowRight className="h-4 w-4" />
                                </a>
                            </div>

                            <span
                                className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
                                style={{ background: `linear-gradient(90deg, ${service.accent}, transparent)` }}
                                aria-hidden="true"
                            />
                        </motion.article>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default CoreServicesSection;
